const GENERIC_HTTP_ERROR = "Edge Function returned a non-2xx status code";

export type EdgeFunctionErrorBody = Record<string, unknown> & {
  error?: string;
  message?: string;
};

function getResponseFromError(error: unknown, response?: Response): Response | undefined {
  if (response) return response;
  if (error && typeof error === "object" && "context" in error) {
    const ctx = (error as { context: unknown }).context;
    if (ctx instanceof Response) return ctx;
  }
  return undefined;
}

export async function parseEdgeFunctionErrorBody(
  error: unknown,
  response?: Response,
): Promise<EdgeFunctionErrorBody | null> {
  const res = getResponseFromError(error, response);
  if (!res) return null;

  try {
    const clone = res.clone();
    const contentType = clone.headers.get("Content-Type") ?? "";

    if (contentType.includes("application/json")) {
      const data = await clone.json();
      return data && typeof data === "object" ? (data as EdgeFunctionErrorBody) : null;
    }

    const text = (await clone.text()).trim();
    if (!text) return null;

    try {
      const data = JSON.parse(text);
      return data && typeof data === "object" ? (data as EdgeFunctionErrorBody) : null;
    } catch {
      return { message: text };
    }
  } catch {
    return null;
  }
}

export async function getEdgeFunctionErrorMessage(
  error: unknown,
  fallback = "Prišlo je do napake. Poskusite znova.",
  response?: Response,
): Promise<string> {
  const body = await parseEdgeFunctionErrorBody(error, response);
  if (body) {
    if (typeof body.message === "string" && body.message) {
      return body.message;
    }
    if (typeof body.error === "string" && body.error) {
      return body.error;
    }
  }

  if (error instanceof Error && error.message && error.message !== GENERIC_HTTP_ERROR) {
    return error.message;
  }

  return fallback;
}
